// KnowledgeFeedListPage.tsx — DEM-56
//
// Structured page (bespoke React + useApi) over the `youtube_watch` table.
// One row per ingested YouTube video. Front-door to a back-catalog that
// the ingest routine fills in continuously. Each row navigates to
// /knowledge-feed/:videoId for the full summary + transcript.
//
// Architecture: renders through the shared <SortableTable>. Locked
// architecture: every list page in the dashboard uses SortableTable so
// the entire row is clickable and keyboard-accessible (skill://dashboard
// section 13, "Don't hand-roll list pages").
//
// Row layout (DEM-56 Erez refinement):
//   - Thumbnail fills its cell edge-to-edge (`object-fit: cover`, zero
//     cell padding via the SortableTable `cellStyle` escape hatch).
//     hqdefault.jpg for crisp 480×360 source. The thumbnail cell sets
//     the row height — every row is the same height, image-forward.
//   - Title clamps to 2 lines, summary clamps to 2 lines, with `…`.
//     Long titles and long summaries no longer blow out the row.
//
// Light-mode is the layout default — /knowledge-feed is added to
// Layout's SKY_BLUE_ROUTES so <main> is painted sky-blue, matching the
// other Structured spine pages (Goals, Projects, Skills).

import { useEffect, useMemo, useState } from "react";
import { Loader2, Tag, AlertTriangle, FileText, Check } from "lucide-react";
import { useApi } from "@/lib/api";
import { architectureT as T } from "../blueprint/ArchitectureBlocks";
import { SortableTable, RowLink, type ColumnDef } from "../blueprint/SortableTable";

type KnowledgeFeedRow = {
  video_id: string;
  title: string | null;
  channel: string | null;
  url: string | null;
  duration_sec: number | null;
  is_short: boolean | null;
  watched_pull_at: string;
  has_transcript: boolean;
  summary: string | null;
  tags: string[] | null;
  topic: string | null;
  is_ai_related: boolean | null;
  is_substantive: boolean | null;
  kb_page_slug: string | null;
  ingest_error: string | null;
};

type Filter = "all" | "substantive" | "ai" | "shorts" | "errors";

// hqdefault is 480x360 (4:3) — `object-fit: cover` crops the letterbox bars
// off so the 16:9 frame looks native. If the video has no hqdefault (rare)
// we fall back to the NO IMG placeholder.
function thumbnailUrl(videoId: string): string {
  return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
}

function formatDuration(sec: number | null): string {
  if (!sec || sec <= 0) return "";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// Thumbnail fills its cell edge-to-edge. SortableTable's per-column
// `cellStyle` zeros the <td> padding so this 16:9 frame defines the row's
// height. Overlays (duration / Shorts pill) keep their corner anchors.
function Thumb({ row }: { row: KnowledgeFeedRow }) {
  const [errored, setErrored] = useState(false);
  const duration = formatDuration(row.duration_sec);
  return (
    <div style={{
      position: "relative",
      width: "100%", height: "100%",
      background: T.bg2,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {errored ? (
        <span style={{ fontSize: 10, color: T.ink3, letterSpacing: "0.04em" }}>NO IMG</span>
      ) : (
        <img
          src={thumbnailUrl(row.video_id)}
          alt=""
          loading="lazy"
          onError={() => setErrored(true)}
          style={{
            width: "100%", height: "100%",
            objectFit: "cover", display: "block",
          }}
        />
      )}
      {duration ? (
        <span style={{
          position: "absolute", insetInlineEnd: 6, bottom: 6,
          background: "rgba(0,0,0,0.78)", color: "#fff",
          fontSize: 11, fontWeight: 600, padding: "2px 6px",
          borderRadius: 3, fontFamily: "ui-monospace, Menlo, monospace",
          letterSpacing: "0.02em",
        }}>{duration}</span>
      ) : null}
      {row.is_short ? (
        <span style={{
          position: "absolute", insetInlineStart: 6, top: 6,
          background: T.plum, color: T.white,
          fontSize: 10, fontWeight: 700, padding: "2px 7px",
          borderRadius: 3, letterSpacing: "0.06em", textTransform: "uppercase",
        }}>Short</span>
      ) : null}
    </div>
  );
}

function TagsCell({ tags, topic }: { tags: string[] | null; topic: string | null }) {
  const items = (tags ?? []).slice(0, 3);
  if (items.length === 0 && !topic) {
    return <span style={{ color: T.ink3 }}>—</span>;
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
      {topic ? (
        <span style={{
          fontSize: 10, fontWeight: 700, color: T.skyDark,
          background: T.skySoft, border: `1px solid ${T.sky}`,
          padding: "2px 8px", borderRadius: 999,
          textTransform: "uppercase", letterSpacing: "0.06em",
          whiteSpace: "nowrap",
        }}>{topic}</span>
      ) : null}
      {items.map((t) => (
        <span key={t} style={{
          fontSize: 11, color: T.ink2,
          background: T.bg2, border: `1px solid ${T.line}`,
          padding: "1px 8px", borderRadius: 999,
          display: "inline-flex", alignItems: "center", gap: 3,
        }}>
          <Tag style={{ width: 9, height: 9, color: T.ink3 }} />
          {t}
        </span>
      ))}
      {(tags?.length ?? 0) > items.length ? (
        <span style={{ fontSize: 11, color: T.ink3 }}>+{(tags?.length ?? 0) - items.length}</span>
      ) : null}
    </div>
  );
}

// 2-line CSS line-clamp. Used for both title and summary so rows stay
// uniform regardless of source-content length. Falls back to plain
// overflow:hidden in browsers without -webkit-line-clamp (vanishingly
// rare today).
const lineClamp2: React.CSSProperties = {
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const COLUMNS: ColumnDef<KnowledgeFeedRow>[] = [
  {
    key: "thumbnail", label: "", width: "200px", sortable: false,
    // Zero padding so the thumbnail bleeds to the cell edges; 16:9 aspect
    // pins row height. verticalAlign:stretch lets the inner flex fill.
    cellStyle: { padding: 0, verticalAlign: "stretch", lineHeight: 0, width: 200 },
    render: (r) => (
      // 16:9 frame anchored to the cell — sets the row height. width:200,
      // height = 200 * 9/16 = 112.5px. The Thumb inside fills 100%.
      <div style={{ width: 200, aspectRatio: "16 / 9", position: "relative" }}>
        <Thumb row={r} />
      </div>
    ),
  },
  {
    key: "title", label: "Title",
    render: (r) => (
      <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
        <RowLink to={`/knowledge-feed/${r.video_id}`}>
          {/* 2-line clamp keeps titles within the row height. */}
          <span style={{ ...lineClamp2, lineHeight: 1.35, fontSize: 15 } as React.CSSProperties}>
            {r.title ?? r.video_id}
          </span>
        </RowLink>
        {r.summary ? (
          <span style={{
            ...lineClamp2,
            fontSize: 12, color: T.ink2, lineHeight: 1.5,
          } as React.CSSProperties}>
            {r.summary.replace(/\s+/g, " ").trim()}
          </span>
        ) : r.ingest_error ? (
          <span style={{
            ...lineClamp2,
            fontSize: 11, color: T.red, lineHeight: 1.5,
          } as React.CSSProperties}>
            <AlertTriangle style={{ width: 11, height: 11, verticalAlign: "-2px", marginRight: 4 }} />
            {r.ingest_error}
          </span>
        ) : (
          <span style={{ fontSize: 12, color: T.ink3, fontStyle: "italic" }}>No summary yet</span>
        )}
      </div>
    ),
  },
  {
    key: "channel", label: "Channel", width: "180px",
    render: (r) => (
      <span style={{ color: T.ink2, fontSize: 13 }}>
        {r.channel && r.channel !== "NA" ? r.channel : <span style={{ color: T.ink3 }}>—</span>}
      </span>
    ),
  },
  {
    key: "tags", label: "Tags / Topic", width: "220px", sortable: false,
    render: (r) => <TagsCell tags={r.tags} topic={r.topic} />,
  },
  {
    key: "kb_page_slug", label: "KB", width: "60px", sortable: false,
    render: (r) => (
      r.kb_page_slug ? (
        <span title={`KB page: ${r.kb_page_slug}`} style={{
          display: "inline-flex", alignItems: "center", gap: 3,
          color: T.green,
        }}>
          <FileText style={{ width: 13, height: 13 }} />
        </span>
      ) : r.has_transcript ? (
        <span title="Transcript captured" style={{
          display: "inline-flex", alignItems: "center", gap: 3,
          color: T.skyDark, fontSize: 11,
        }}>
          <Check style={{ width: 13, height: 13 }} />
        </span>
      ) : (
        <span style={{ color: T.ink3 }}>—</span>
      )
    ),
  },
  {
    key: "watched_pull_at", label: "Watched", width: "110px",
    render: (r) => (
      <span style={{ color: T.ink3, fontSize: 12, whiteSpace: "nowrap" }}>
        {new Date(r.watched_pull_at).toLocaleDateString(undefined, {
          year: "2-digit", month: "short", day: "numeric",
        })}
      </span>
    ),
  },
];

const FILTERS: { id: Filter; label: string; predicate: (r: KnowledgeFeedRow) => boolean }[] = [
  { id: "all",         label: "All",          predicate: () => true },
  { id: "substantive", label: "Substantive",  predicate: (r) => r.is_substantive === true },
  { id: "ai",          label: "AI-related",   predicate: (r) => r.is_ai_related === true },
  { id: "shorts",      label: "Shorts",       predicate: (r) => r.is_short === true },
  { id: "errors",      label: "Errors",       predicate: (r) => !!r.ingest_error },
];

export function KnowledgeFeedListPage() {
  const api = useApi();
  const [rows, setRows] = useState<KnowledgeFeedRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [query, setQuery] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api("/api/knowledge-feed");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as KnowledgeFeedRow[];
        if (!cancelled) setRows(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return null;
    const pred = FILTERS.find(f => f.id === filter)?.predicate ?? (() => true);
    const q = query.trim().toLowerCase();
    return rows.filter(r => {
      if (!pred(r)) return false;
      if (!q) return true;
      const hay = [
        r.title, r.channel, r.summary, r.topic, r.video_id,
        (r.tags ?? []).join(" "),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [rows, filter, query]);

  const counts = useMemo(() => {
    if (!rows) return null;
    const map: Record<Filter, number> = { all: 0, substantive: 0, ai: 0, shorts: 0, errors: 0 };
    for (const r of rows) {
      map.all++;
      if (r.is_substantive === true) map.substantive++;
      if (r.is_ai_related === true) map.ai++;
      if (r.is_short === true) map.shorts++;
      if (r.ingest_error) map.errors++;
    }
    return map;
  }, [rows]);

  return (
    <div style={{
      fontFamily: "Inter, sans-serif",
      boxSizing: "border-box",
      padding: "40px 48px 80px",
    }}>
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <div style={{ textAlign: "center", padding: "32px 20px 32px", marginBottom: 24 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, letterSpacing: "0.1em",
            color: T.skyDark, textTransform: "uppercase", marginBottom: 14,
          }}>
            MyJarvis · Knowledge intake
          </div>
          <h1 style={{
            fontSize: 34, fontWeight: 800, color: T.ink,
            margin: "0 0 14px", letterSpacing: "-0.02em", lineHeight: 1.15,
          }}>
            Knowledge Feed
          </h1>
          <p style={{
            fontSize: 16, color: T.ink2, lineHeight: 1.65,
            maxWidth: 720, margin: "0 auto",
          }}>
            Everything Erez has watched on YouTube — ingested, transcribed, and summarized. One row per video. Click any row for the full summary, transcript, and any cross-linked KB pages.
          </p>
        </div>

        {/* Filter bar — chips + search input. Visible even while loading so the
            chrome doesn't pop in late. */}
        <div style={{
          display: "flex", flexWrap: "wrap", gap: 10,
          alignItems: "center", justifyContent: "space-between",
          marginBottom: 18,
        }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {FILTERS.map(f => {
              const active = f.id === filter;
              const count = counts?.[f.id] ?? 0;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFilter(f.id)}
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: 12, fontWeight: 600,
                    color: active ? T.white : T.ink2,
                    background: active ? T.skyDark : T.white,
                    border: `1px solid ${active ? T.skyDark : T.line}`,
                    borderRadius: 999,
                    padding: "6px 14px",
                    cursor: "pointer",
                    display: "inline-flex", alignItems: "center", gap: 6,
                    transition: "background 0.12s, color 0.12s, border 0.12s",
                  }}
                >
                  {f.label}
                  {counts ? (
                    <span style={{
                      fontSize: 11, fontWeight: 700,
                      color: active ? T.white : T.ink3,
                      opacity: active ? 0.9 : 1,
                    }}>{count}</span>
                  ) : null}
                </button>
              );
            })}
          </div>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search title, channel, summary, tags…"
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: 13, color: T.ink,
              background: T.white,
              border: `1px solid ${T.line}`,
              borderRadius: 8,
              padding: "8px 12px",
              minWidth: 280,
              outline: "none",
            }}
          />
        </div>

        {error ? (
          <div style={{
            padding: "16px 20px", color: T.red, background: T.redSoft,
            border: `1px solid ${T.red}`, borderRadius: 8, fontSize: 13,
          }}>
            Failed to load knowledge feed: {error}
          </div>
        ) : rows === null || filtered === null ? (
          <div style={{
            padding: 24, fontSize: 14, color: T.ink3,
            display: "inline-flex", alignItems: "center", gap: 8,
          }}>
            <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} />
            Loading…
          </div>
        ) : (
          <SortableTable
            rows={filtered as unknown as Record<string, unknown>[]}
            columns={COLUMNS as unknown as ColumnDef<Record<string, unknown>>[]}
            detailHref={(r) => `/knowledge-feed/${(r as unknown as KnowledgeFeedRow).video_id}`}
            rowKey={(r) => (r as unknown as KnowledgeFeedRow).video_id}
            defaultSort={{ key: "watched_pull_at", dir: "desc" }}
            emptyMessage={
              rows.length === 0
                ? "No videos ingested yet. Rows appear once the watch-pull routine runs."
                : "No matches for the current filter."
            }
          />
        )}
      </div>
    </div>
  );
}

(KnowledgeFeedListPage as unknown as { path: string }).path = "/knowledge-feed";
